import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { OneTrustConsentGroups } from '~/enums';

const useConsent = () => {
  const postHog = usePostHog();
  const [consentData, setConsentData] = useState<string[]>(
    () =>
      window.OnetrustActiveGroups?.split(',').filter(
        (g) => g.trim().length > 0
      ) ?? []
  );

  const handleEventCapturing = (userAcceptedTracking: boolean) => {
    const alreadyOptedIn = postHog.has_opted_in_capturing();

    if (userAcceptedTracking && !alreadyOptedIn) {
      postHog.opt_in_capturing({
        captureEventName: 'user_opted_in_event_capturing',
      });
    } else if (!userAcceptedTracking && alreadyOptedIn) {
      postHog.opt_out_capturing();
    }
  };

  const handleSessionRecording = (userAcceptedTracking: boolean) => {
    const recordingAlreadyStarted = postHog.sessionRecordingStarted();

    if (userAcceptedTracking && !recordingAlreadyStarted) {
      postHog.startSessionRecording();
    } else if (!userAcceptedTracking && recordingAlreadyStarted) {
      postHog.stopSessionRecording();
    }
  };

  useEffect(() => {
    const readDataAndSetConsentPref = (event: Event) => {
      if (window.OneTrust && event instanceof CustomEvent) {
        setConsentData(event.detail);
      }
    };

    window.addEventListener('consent.onetrust', readDataAndSetConsentPref);

    return () => {
      window.removeEventListener('consent.onetrust', readDataAndSetConsentPref);
    };
  }, []);

  useEffect(() => {
    if (!postHog) return;

    if (import.meta.env.VITE_POSTHOG_TRACKING_REQUIRES_CONSENT === 'true') {
      const userAcceptedTracking = consentData.includes(
        OneTrustConsentGroups.PerformanceCookies
      );

      postHog.setPersonProperties({
        opted_out_tracking: !userAcceptedTracking,
      });

      if (import.meta.env.VITE_POSTHOG_EVENT_CAPTURING_ENABLED === 'true') {
        handleEventCapturing(userAcceptedTracking);
      }

      if (import.meta.env.VITE_POSTHOG_SESSION_RECORDING_ENABLED === 'true') {
        handleSessionRecording(userAcceptedTracking);
      }
    }
  }, [consentData, postHog, handleEventCapturing, handleSessionRecording]);
};

export default useConsent;
